import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { updateAdminExperiencePreference } from '../services/access'
import type { AdminMode } from '../types/access'
import { useAccessContext } from './AccessContext'

interface AdminExperienceContextValue {
  mode: AdminMode
  isSimple: boolean
  isAdvanced: boolean
  guidedEnabled: boolean
  guidedRequired: boolean
  guidedAvailable: boolean
  setGuidedEnabled: (enabled: boolean) => void
  allowModeToggle: boolean
  rememberUserPreference: boolean
  advancedOptionsExpanded: boolean
  setAdvancedOptionsExpanded: (expanded: boolean) => void
  setMode: (mode: AdminMode) => Promise<void>
}

const readGuidedPreference = (key: string): boolean | null => {
  try {
    const stored = window.localStorage.getItem(key)
    return stored === 'true' ? true : stored === 'false' ? false : null
  } catch { return null }
}

const AdminExperienceContext = createContext<AdminExperienceContextValue | undefined>(undefined)

export const AdminExperienceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accessContext, refresh } = useAccessContext()
  const resolved = accessContext?.adminExperience
  const resolvedMode: AdminMode = resolved?.effectiveMode === 'advanced' ? 'advanced' : 'simple'
  const [localModeOverride, setLocalModeOverride] = useState<AdminMode | null>(null)
  const mode: AdminMode = localModeOverride || resolvedMode
  const [advancedOptionsExpanded, setAdvancedOptionsExpanded] = useState(false)
  const guidedMode = resolved?.guidedWorkflows || 'default_on'
  const guidedPreferenceKey = `punch.guidedWorkflowsEnabled.${accessContext?.tenantId || 'platform'}`
  const [guidedPreference, setGuidedPreference] = useState<boolean | null>(() => {
    if (typeof window === 'undefined') return null
    return readGuidedPreference(`punch.guidedWorkflowsEnabled.${accessContext?.tenantId || 'platform'}`)
  })

  const setGuidedEnabled = useCallback((enabled: boolean) => {
    if (guidedMode === 'required' || guidedMode === 'disabled') return
    setGuidedPreference(enabled)
    try { window.localStorage.setItem(guidedPreferenceKey, String(enabled)) } catch { /* Keep the mounted choice. */ }
  }, [guidedMode, guidedPreferenceKey])

  const setMode = useCallback(async (nextMode: AdminMode) => {
    if (!resolved?.allowAdvancedModeToggle || nextMode === mode) return
    if (resolved.rememberUserPreference) {
      await updateAdminExperiencePreference(nextMode)
      await refresh()
    } else {
      setLocalModeOverride(nextMode)
    }
    setAdvancedOptionsExpanded(false)
  }, [mode, refresh, resolved?.allowAdvancedModeToggle, resolved?.rememberUserPreference])

  useEffect(() => {
    setLocalModeOverride(null)
  }, [accessContext?.tenantId, resolvedMode])

  useEffect(() => {
    if (typeof window === 'undefined') return
    setGuidedPreference(readGuidedPreference(guidedPreferenceKey))
  }, [guidedPreferenceKey])

  const value = useMemo<AdminExperienceContextValue>(() => ({
    mode,
    isSimple: mode === 'simple',
    isAdvanced: mode === 'advanced',
    guidedEnabled: guidedMode === 'required'
      || (guidedMode === 'default_on' && (guidedPreference ?? true))
      || (guidedMode === 'available' && (guidedPreference ?? false)),
    guidedRequired: guidedMode === 'required',
    guidedAvailable: guidedMode !== 'disabled',
    setGuidedEnabled,
    allowModeToggle: Boolean(resolved?.allowAdvancedModeToggle),
    rememberUserPreference: resolved?.rememberUserPreference ?? false,
    advancedOptionsExpanded,
    setAdvancedOptionsExpanded,
    setMode,
  }), [advancedOptionsExpanded, guidedMode, guidedPreference, mode, resolved?.allowAdvancedModeToggle, resolved?.rememberUserPreference, setGuidedEnabled, setMode])

  return <AdminExperienceContext.Provider value={value}>{children}</AdminExperienceContext.Provider>
}

export const useAdminExperience = () => {
  const context = useContext(AdminExperienceContext)
  if (!context) throw new Error('useAdminExperience must be used within AdminExperienceProvider')
  return context
}

export const SimpleOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSimple } = useAdminExperience()
  return isSimple ? <>{children}</> : null
}

export const AdvancedOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdvanced } = useAdminExperience()
  return isAdvanced ? <>{children}</> : null
}

export const AdvancedOptions: React.FC<{
  children: React.ReactNode
  label?: string
  className?: string
}> = ({ children, label = 'Advanced options', className }) => {
  const { isAdvanced } = useAdminExperience()
  const [expanded, setExpanded] = useState(false)
  if (isAdvanced) return <div className={className}>{children}</div>
  return (
    <div className={className} data-testid="advanced-options">
      <button
        type="button"
        className="btn btn-secondary"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        {label}
      </button>
      {expanded ? <div className="stack">{children}</div> : null}
    </div>
  )
}

export const GuidedHelp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { guidedEnabled } = useAdminExperience()
  return guidedEnabled ? <>{children}</> : null
}
